'use client';

import { FocusEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BadgeProps, Box, FormHelperText } from "@mui/material";
import { BoxProps } from "@mui/system";
import { Markdown, MarkdownProps } from "@/app/features/shared/markdown/Markdown";
import MarkdownEditor, { MarkdownEditorProps } from "@/app/features/shared/editable-markdown/MarkdownEditor";

type EditableMarkdownV2Props = {
    value: string | undefined;
    onChange: (value: string) => void;
    onEditingChange?: (isEditing: boolean) => void;
    fitToAvailableHeightOnEdit?: boolean;
    label?: string;
    required?: boolean;
    error?: boolean;
    helperText?: string;
    disabled?: boolean;
    commitMode?: 'change' | 'blur';
    ariaLabel?: string;
    typographyOverrides?: MarkdownProps["typographyOverrides"];
    slots?: {
        root?: BoxProps;
        badge?: BadgeProps;
        markdown?: MarkdownProps["slots"] & {
            editor?: Partial<MarkdownEditorProps>;
        };
    };
};

export default function EditableMarkdownV2({
    value,
    onChange,
    onEditingChange,
    fitToAvailableHeightOnEdit = true,
    label,
    required = false,
    error = false,
    helperText,
    disabled = false,
    commitMode = 'change',
    ariaLabel = "Markdown bearbeiten",
    typographyOverrides,
    slots,
}: EditableMarkdownV2Props) {
    const [isEditing, setIsEditing] = useState(false);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const lastPointerDownTargetRef = useRef<EventTarget | null>(null);
    const latestValueRef = useRef(value ?? "");
    const resolvedValue = value ?? "";
    const isValueEmpty = resolvedValue.length === 0;
    const placeholderBaseText = label ?? "";
    const placeholderDisplayText = required && placeholderBaseText
        ? `${placeholderBaseText}*`
        : placeholderBaseText;
    const viewerValue = isValueEmpty ? (placeholderDisplayText || undefined) : resolvedValue;
    const editorLabel = label ? placeholderDisplayText : undefined;
    const hasPlaceholderView = !isEditing && isValueEmpty && Boolean(placeholderDisplayText);
    const rootSlotSx = slots?.root?.sx;

    const rootSx = useMemo(() => {
        const baseSx = hasPlaceholderView
            ? { color: error ? "error.main" : "text.disabled" }
            : undefined;
        const editingFitSx = isEditing && fitToAvailableHeightOnEdit
            ? { display: 'flex', flex: '1 1 auto', minHeight: 0, minWidth: 0 }
            : undefined;
        if (Array.isArray(rootSlotSx)) {
            const sxList = [];
            if (baseSx) sxList.push(baseSx);
            if (editingFitSx) sxList.push(editingFitSx);
            return [...sxList, ...rootSlotSx];
        }
        if (rootSlotSx) {
            const sxList = [];
            if (baseSx) sxList.push(baseSx);
            if (editingFitSx) sxList.push(editingFitSx);
            return [...sxList, rootSlotSx];
        }
        return {
            ...baseSx,
            ...editingFitSx,
        };
    }, [error, fitToAvailableHeightOnEdit, hasPlaceholderView, isEditing, rootSlotSx]);

    const isInsideEditorSurface = useCallback((target: EventTarget | null) => {
        if (!(target instanceof Element)) return false;
        if (rootRef.current?.contains(target)) return true;
        return Boolean(target.closest('#headings-menu, #text-size-menu'));
    }, []);

    const onEditorBlur = useCallback((event?: FocusEvent<HTMLElement>) => {
        const relatedTarget = event?.relatedTarget ?? null;
        if (isInsideEditorSurface(relatedTarget) || isInsideEditorSurface(lastPointerDownTargetRef.current)) {
            return;
        }

        if (commitMode === 'blur' && latestValueRef.current !== resolvedValue) {
            onChange(latestValueRef.current);
        }
        setIsEditing(false);
    }, [commitMode, isInsideEditorSurface, onChange, resolvedValue]);

    const startEditing = useCallback(() => {
        if (disabled) return;
        latestValueRef.current = resolvedValue;
        setIsEditing(true);
    }, [disabled, resolvedValue]);

    const onEditorChange = useCallback((nextValue: string) => {
        latestValueRef.current = nextValue;
        if (commitMode === 'change') onChange(nextValue);
    }, [commitMode, onChange]);

    useEffect(() => {
        onEditingChange?.(isEditing);
    }, [isEditing, onEditingChange]);

    useEffect(() => {
        return () => {
            onEditingChange?.(false);
        };
    }, [onEditingChange]);

    useEffect(() => {
        if (!isEditing) return;

        const handlePointerDown = (event: PointerEvent) => {
            lastPointerDownTargetRef.current = event.target;
        };

        window.addEventListener('pointerdown', handlePointerDown, true);
        return () => {
            window.removeEventListener('pointerdown', handlePointerDown, true);
            lastPointerDownTargetRef.current = null;
        };
    }, [isEditing]);

    const editingMarkdownRootSx = useMemo(() => {
        const borderSx = {
            boxSizing: 'border-box',
            border: 1,
            borderColor: 'primary.main',
            borderRadius: 1,
            padding: 2,
            transition: 'border-color 0.2s ease',
        };
        const constrainedHeightSx = {
            display: "flex",
            flex: "1 1 auto",
                maxHeight: "100%",
                minHeight: 0,
                minWidth: 0,
            '& .markdown-editor': {
                height: "100%",
                maxHeight: "100%",
                minHeight: 0,
                overflow: "visible",
            },
            '& .markdown-editor > .MuiBox-root': {
                minHeight: 0,
            },
            '& .markdown-editor-content .ProseMirror': {
                minHeight: 0,
            },
        };
        const currentRootSx = slots?.markdown?.root?.sx;

        if (Array.isArray(currentRootSx)) return [borderSx, ...currentRootSx, constrainedHeightSx];
        if (currentRootSx) return [borderSx, currentRootSx, constrainedHeightSx];
        return [borderSx, constrainedHeightSx];
    }, [slots?.markdown?.root?.sx]);

    return (
        <Box ref={rootRef} {...slots?.root} sx={rootSx}>
            {isEditing
                ? (
                    <MarkdownEditor
                        value={resolvedValue}
                        onChange={onEditorChange}
                        label={editorLabel}
                        onBlur={onEditorBlur}
                        error={error}
                        helperText={helperText}
                        autoFocus
                        constrainToContainer
                        typographyOverrides={typographyOverrides}
                        slots={{
                            root: {
                                ...slots?.markdown?.root,
                                sx: editingMarkdownRootSx,
                            },
                        }}
                        {...slots?.markdown?.editor}
                    />
                )
                : (
                    <Box
                        onClick={startEditing}
                        onKeyDown={(e) => {
                            if (disabled) return;
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                startEditing();
                            }
                        }}
                        role="button"
                        tabIndex={disabled ? -1 : 0}
                        aria-label={ariaLabel}
                        sx={{
                            boxSizing: 'border-box',
                            width: '100%',
                            minWidth: 0,
                            alignSelf: 'flex-start',
                            border: 1,
                            borderColor: 'breakout.main',
                            borderRadius: 1,
                            padding: 2,
                            cursor: disabled ? 'default' : 'pointer',
                            '&:hover': disabled ? {} : {
                                borderColor: 'breakout.light',
                            },
                            '&:focus-visible': {
                                outline: 2,
                                outlineStyle: 'solid',
                                outlineColor: 'breakout.main',
                                outlineOffset: 2,
                            },
                        }}
                    >
                        <Markdown
                            value={viewerValue}
                            typographyOverrides={typographyOverrides}
                            slots={slots?.markdown}
                        />
                        {helperText && (
                            <FormHelperText
                                error={error}
                                sx={{ mt: 0.5, mx: 0 }}
                            >
                                {helperText}
                            </FormHelperText>
                        )}
                    </Box>
                )}
        </Box>
    );
}
